// ============================================================
// bodyMapModel - what the body map draws, as pure answers
// ============================================================
// The trait chips and whether the rails have anything to show. The body
// plates draw everything else (components/plate, engine/plate), their
// conditions read by plateState alone; nothing here touches React.

import type { Target } from '../types';

/** One trait chip: a label and, for a rated trait, its rating. */
export interface TraitItem {
  label: string;
  value?: number;
}

type TraitSource = Partial<Target>;

// In chip order. Astartes implies True Grit (v6.26+) - show ONE Astartes
// badge, not both.
const FLAG_TRAITS: { label: string; on: (t: TraitSource) => boolean }[] = [
  { label: 'True Grit', on: (t) => !!t.trueGrit && !t.astartes },
  { label: 'Stuff of Nightmares', on: (t) => !!t.stuffOfNightmares },
  { label: 'Incorporeal', on: (t) => !!t.incorporeal },
  { label: 'From Beyond', on: (t) => !!t.fromBeyond },
  { label: 'Astartes', on: (t) => !!t.astartes },
  { label: 'Undying', on: (t) => !!t.undying },
  { label: 'Xenos', on: (t) => !!t.xenos },
];

type RatedKey = 'daemonicProtection' | 'machineAP' | 'naturalArmour' | 'regeneration' | 'fearRating';

const RATED_TRAITS: { label: string; key: RatedKey }[] = [
  { label: 'Daemonic', key: 'daemonicProtection' },
  { label: 'Machine', key: 'machineAP' },
  { label: 'Natural Armour', key: 'naturalArmour' },
  { label: 'Regeneration', key: 'regeneration' },
  { label: 'Fear', key: 'fearRating' },
];

/** Character traits the flat fields above already speak for. */
const MECHANICAL_TRAIT_IDS = [
  'true_grit',
  'stuff_of_nightmares',
  'incorporeal',
  'from_beyond',
  'astartes',
  'undying',
  'daemonic',
  'machine',
  'natural_armour',
  'regeneration',
  'fear',
];

interface CharacterTraits {
  traits?: { traitId: string; parameter?: string }[];
}

/**
 * Trait chips derived from the target's FLAT FIELDS, which are the source
 * of truth - a disabled mechanical trait must never reappear via
 * sourceCharacter.traits. Non-mechanical character traits (narrative etc.)
 * are appended, once each.
 */
export function buildTargetTraitItems(target: TraitSource, sourceCharacter?: CharacterTraits | null): TraitItem[] {
  const flags = FLAG_TRAITS.filter((t) => t.on(target)).map(({ label }) => ({ label }));
  const rated = RATED_TRAITS.filter((t) => (target[t.key] || 0) > 0).map((t) => ({
    label: t.label,
    value: target[t.key],
  }));
  return [...flags, ...rated, ...narrativeTraits(sourceCharacter)];
}

function narrativeTraits(sourceCharacter?: CharacterTraits | null): TraitItem[] {
  const seen = new Set(MECHANICAL_TRAIT_IDS);
  const items: TraitItem[] = [];
  for (const t of sourceCharacter?.traits ?? []) {
    if (seen.has(t.traitId)) continue;
    seen.add(t.traitId);
    const label = t.traitId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    items.push({ label, value: t.parameter ? parseInt(t.parameter) || undefined : undefined });
  }
  return items;
}
